import { useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';

import { useAbilityContext } from '@/contexts/ability';
import {
  getNotifications,
  markNotification,
  getNotificationDetail,
  markAllNotificationsAsRead,
} from '../../../../services/Notifications';
import { formatDate, isTodayOrFuture } from '../../../../utils/date';
import { useNavigateSearch } from '../../../../hooks/useNavigate';

import { useAppContext } from '../../../../contexts/app';
import { useNotificationsContext } from '../../../../contexts/notifications';

const updateNotification = (id, read) => (notification) => {
  const notificationRead = notification.id === id ? !read : notification.read;

  return {
    ...notification,
    read: notificationRead,
  };
};

const filterBy = ({ onlyUnRead }) => (notification) => (
  onlyUnRead ? notification.read === false : true
);

const useNotificationsList = (type, handleClose, initialFilters) => {
  const ability = useAbilityContext();
  const { isLoading, setAppLoading, handleAlert } = useAppContext();
  const { newNotifications, changeNewNotifications } = useNotificationsContext();

  const navigateSearch = useNavigateSearch();
  const componentRef = useRef();

  const [loading, setLoading] = useState(false);
  const [onlyUnRead, setOnlyUnRead] = useState(() => type === 'toolbar');

  const [pageSize, setPageSize] = useState(50);
  const [pageNumber, setPageNumber] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const [notifications, setNotifications] = useState([]);

  useEffect(() => setAppLoading({ [`${type}Loader`]: true }), []);

  const handleBlockScreen = (block) => {
    if (type === 'toolbar') {
      setLoading(block);
    } else {
      setAppLoading({ page: block });
    }
  };

  const initComponent = () => {
    handleBlockScreen(true);

    Promise.all([
      getNotifications({ page: pageNumber, onlyUnread: onlyUnRead, ...initialFilters }),
      getNotifications({ page: pageNumber, onlyUnread: true, ...initialFilters }),
    ]).then(([allNotifications, unreadNotifications]) => {
      if (type === 'toolbar' && pageNumber !== 0) {
        setNotifications([
          ...notifications,
          ...allNotifications.content,
        ]);
      } else {
        setNotifications(allNotifications.content);
      }
      changeNewNotifications(unreadNotifications?.totalElements);
      setPageSize(allNotifications.pageSize);
      setPageNumber(allNotifications.pageNumber);
      setTotalElements(allNotifications.totalElements);
      handleBlockScreen(false);
    }).catch((error) => {
      handleAlert(error, 'error');
      handleBlockScreen(false);
    }).finally(() => {
      setAppLoading({ [`${type}Loader`]: false });
    });
  };

  useEffect(initComponent, [pageNumber, onlyUnRead]);

  const handleOnScroll = () => {
    if (type === 'toolbar' && componentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = componentRef.current;
      if (scrollTop + clientHeight === scrollHeight) {
        setPageNumber((prevPageNumber) => prevPageNumber + 1);
      }
    }
  };

  const handleChangePage = (event, value) => setPageNumber(value);

  const handleChangeSwitch = () => {
    setPageNumber(0);
    setOnlyUnRead(!onlyUnRead);
  };

  const handleRead = (id, read) => {
    handleBlockScreen(true);

    Promise.all([
      markNotification(id, !read),
    ]).then(() => {
      const updatedNotifications = notifications
        .map(updateNotification(id, read))
        .filter(filterBy({ onlyUnRead }));

      setNotifications(updatedNotifications);

      Promise.all([
        getNotifications({ page: pageNumber, onlyUnread: true, ...initialFilters }),
      ]).then(([unreadNotifications]) => {
        changeNewNotifications(unreadNotifications?.totalElements);
        handleBlockScreen(false);
      });
    }).catch((error) => {
      handleAlert(error, 'error');
      handleBlockScreen(false);
    });
  };

  const handleView = async (id) => {
    handleBlockScreen(true);

    try {
      const { payload, constraintResource, constraintValue } = await getNotificationDetail(id);

      if (type === 'toolbar') {
        handleClose();
      }

      if (constraintResource === 'TRANSACTION') {
        navigateSearch('/transactions', {
          transactionId: payload.metadata.transactionId,
          institutionId: payload.institutionId,
        });
      } else if (constraintResource === 'ACCOUNT') {
        navigateSearch('/accounts', {
          accountId: payload.accountData.id,
          institutionId: payload.institutionId,
        });
      } else if (constraintResource === 'INVESTMENT_APPLICATION') {
        const baseUrl = ability.can('enabled', 'investments-v2')
          ? '/v2/investments/portfolio/deposit' : '/investments/applications/deposit';
        navigateSearch(baseUrl, {
          dueDate: [constraintValue, formatDate(moment(constraintValue).add(1, 'day'), 'YYYY-MM-DD')],
        });
      }
    } catch (error) {
      handleAlert(error, 'error');
    } finally {
      handleBlockScreen(false);
    }
  };

  const handleReadAll = () => {
    handleBlockScreen(true);

    Promise.all([
      markAllNotificationsAsRead({ ...initialFilters }),
    ]).then(() => {
      if (type === 'toolbar' && componentRef.current) {
        componentRef.current.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
      setTimeout(() => {
        setPageNumber(0);
        initComponent();
      }, 500);
    }).catch((error) => {
      handleAlert(error, 'error');
      handleBlockScreen(false);
    });
  };

  return {
    loading,
    isLoading,
    pageSize,
    onlyUnRead,
    componentRef,
    pageNumber,
    totalElements,
    newNotifications,
    todayNotifications: notifications.filter((el) => isTodayOrFuture(el.createdAt)),
    historyNotifications: notifications.filter((el) => !isTodayOrFuture(el.createdAt)),
    handleRead,
    handleView,
    handleReadAll,
    handleOnScroll,
    handleChangePage,
    handleChangeSwitch,
  };
};

export default useNotificationsList;
